import type { AxiosError } from "axios";
import type { UpdateSeriesBookingTimesResponse } from "../../../features/Schedule/schedule.types";

/**
 * Type guard for a series time-edit conflict: HTTP 409 with a
 * `blockedBookings` + `freeBookingIds` payload from update-series-booking-times
 * (all-or-nothing mode). Used to open the conflict dialog instead of a generic error toast.
 */
export function isSeriesTimeUpdateConflict(
  error: unknown
): error is AxiosError<UpdateSeriesBookingTimesResponse> {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return false;
  }
  const response = (error as AxiosError<UpdateSeriesBookingTimesResponse>).response;
  if (response?.status !== 409) {
    return false;
  }
  const data = response.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "blockedBookings" in data &&
    Array.isArray(data.blockedBookings) &&
    "freeBookingIds" in data &&
    Array.isArray(data.freeBookingIds)
  );
}

/** Returns the series conflict body when `error` is a 409 conflict; otherwise `null`. */
export function getSeriesConflictPayload(error: unknown): UpdateSeriesBookingTimesResponse | null {
  if (!isSeriesTimeUpdateConflict(error)) {
    return null;
  }
  return error.response?.data ?? null;
}
